import { useState, useMemo } from 'react';
import {
  orderBy,
  where,
  limit,
  type QueryConstraint,
  Timestamp,
} from 'firebase/firestore';
import {
  Activity,
  Calendar,
  Filter,
  Globe,
  Search,
  User,
} from 'lucide-react';
import MainLayout from '../../components/layout/MainLayout';
import { Badge } from '../../components/ui/Badge';
import { Input } from '../../components/ui/Input';
import { useCollection } from '../../hooks/useFirestore';
import {
  CATEGORIA_LABELS,
  type CategoriaAccion,
  type RegistroAcceso,
} from '../../types';
import styles from './AuditoriaAccesosPage.module.css';

const CATEGORIAS = Object.entries(CATEGORIA_LABELS) as [CategoriaAccion, string][];

const categoriaVariant = (
  cat: CategoriaAccion,
): 'success' | 'error' | 'warning' | 'info' | 'neutral' => {
  switch (cat) {
    case 'acceso':
      return 'info';
    case 'navegacion':
      return 'neutral';
    case 'clinica':
      return 'success';
    case 'admin':
      return 'warning';
    case 'perfil':
      return 'info';
    default:
      return 'neutral';
  }
};

const accionVariant = (
  accion: string,
): 'success' | 'error' | 'warning' | 'info' | 'neutral' => {
  if (accion.includes('FINALIZADA') || accion === 'LOGOUT') return 'neutral';
  if (accion.includes('INICIADA') || accion === 'LOGIN' || accion === 'SESSION_START')
    return 'success';
  if (accion === 'PAGE_VIEW') return 'info';
  if (
    accion.includes('CREADA') ||
    accion.includes('EDITADA') ||
    accion.includes('PREREGISTRADO') ||
    accion.includes('MODIFICADA')
  )
    return 'warning';
  return 'neutral';
};

function todayStr(): string {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function toStartOfDay(dateStr: string): Date {
  const [y, m, d] = dateStr.split('-').map(Number);
  return new Date(y, m - 1, d, 0, 0, 0);
}

function toEndOfDay(dateStr: string): Date {
  const [y, m, d] = dateStr.split('-').map(Number);
  return new Date(y, m - 1, d, 23, 59, 59);
}

export default function AuditoriaAccesosPage() {
  const [search, setSearch] = useState('');
  const [filterCategoria, setFilterCategoria] = useState('');
  const [filterFechaDesde, setFilterFechaDesde] = useState(todayStr);
  const [filterFechaHasta, setFilterFechaHasta] = useState(todayStr);

  const constraints: QueryConstraint[] = useMemo(() => {
    const c: QueryConstraint[] = [orderBy('creadoEn', 'desc'), limit(300)];

    if (filterCategoria) {
      c.unshift(where('categoria', '==', filterCategoria));
    }

    if (filterFechaDesde) {
      c.unshift(
        where('creadoEn', '>=', Timestamp.fromDate(toStartOfDay(filterFechaDesde))),
      );
    }

    if (filterFechaHasta) {
      c.unshift(
        where('creadoEn', '<=', Timestamp.fromDate(toEndOfDay(filterFechaHasta))),
      );
    }

    return c;
  }, [filterCategoria, filterFechaDesde, filterFechaHasta]);

  const { data: logsRaw, loading } = useCollection<RegistroAcceso>(
    'auditoria_accesos',
    constraints,
  );
  const logs = useMemo(() => logsRaw || [], [logsRaw]);

  const filtered = search
    ? logs.filter((l) => {
        const q = search.toLowerCase();
        return (
          l.email?.toLowerCase().includes(q) ||
          l.pagina?.toLowerCase().includes(q) ||
          l.accion?.toLowerCase().includes(q) ||
          l.descripcion?.toLowerCase().includes(q) ||
          l.uid?.toLowerCase().includes(q)
        );
      })
    : logs;

  // Estadísticas rápidas
  const stats = useMemo(() => {
    const uniqueUsers = new Set(logs.map((l) => l.uid));
    const pageViews = logs.filter((l) => l.accion === 'PAGE_VIEW').length;
    const actions = logs.filter((l) => l.accion !== 'PAGE_VIEW').length;
    return { uniqueUsers: uniqueUsers.size, pageViews, actions };
  }, [logs]);

  const formatDate = (ts: unknown) => {
    if (!ts) return '-';
    const t = ts as { toDate?: () => Date; seconds?: number };
    const date = t.toDate ? t.toDate() : t.seconds ? new Date(t.seconds * 1000) : null;
    if (!date) return '-';
    return date.toLocaleString('es-CR', { dateStyle: 'short', timeStyle: 'medium' });
  };

  return (
    <MainLayout>
      <div className={styles.page}>
        {/* Header */}
        <div className={styles.header}>
          <div>
            <h1 className={styles.title}>
              <Activity size={22} /> Auditoria de Accesos
            </h1>
            <p className={styles.subtitle}>
              Registro de accesos, paginas visitadas y acciones realizadas. Ultimos 300
              eventos.
            </p>
          </div>
        </div>

        {/* Stats cards */}
        <div className={styles.statsRow}>
          <div className={styles.statCard}>
            <User size={16} className={styles.statIcon} />
            <div>
              <span className={styles.statValue}>{stats.uniqueUsers}</span>
              <span className={styles.statLabel}>Usuarios unicos</span>
            </div>
          </div>
          <div className={styles.statCard}>
            <Globe size={16} className={styles.statIcon} />
            <div>
              <span className={styles.statValue}>{stats.pageViews}</span>
              <span className={styles.statLabel}>Paginas visitadas</span>
            </div>
          </div>
          <div className={styles.statCard}>
            <Activity size={16} className={styles.statIcon} />
            <div>
              <span className={styles.statValue}>{stats.actions}</span>
              <span className={styles.statLabel}>Acciones registradas</span>
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className={styles.filters}>
          <div className={styles.searchBox}>
            <Input
              placeholder="Buscar por email, pagina, accion..."
              leftIcon={<Search size={16} />}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>

          <div className={styles.filterSelect}>
            <Filter size={14} />
            <select
              value={filterCategoria}
              onChange={(e) => setFilterCategoria(e.target.value)}
            >
              <option value="">Todas las categorias</option>
              {CATEGORIAS.map(([key, label]) => (
                <option key={key} value={key}>
                  {label}
                </option>
              ))}
            </select>
          </div>

          <div className={styles.dateRange}>
            <Calendar size={14} />
            <input
              type="date"
              className={styles.dateInput}
              value={filterFechaDesde}
              onChange={(e) => setFilterFechaDesde(e.target.value)}
              placeholder="Desde"
            />
            <span className={styles.dateSep}>—</span>
            <input
              type="date"
              className={styles.dateInput}
              value={filterFechaHasta}
              onChange={(e) => setFilterFechaHasta(e.target.value)}
              placeholder="Hasta"
            />
          </div>
        </div>

        {/* Table */}
        <div className={styles.tableCard}>
          {loading ? (
            <p className={styles.loading}>Cargando registros...</p>
          ) : filtered.length === 0 ? (
            <p className={styles.empty}>No se encontraron registros de acceso.</p>
          ) : (
            <div className={styles.tableScroll}>
              <table className={styles.table}>
                <thead>
                  <tr>
                    <th>Fecha</th>
                    <th>Usuario</th>
                    <th>Rol</th>
                    <th>Accion</th>
                    <th>Categoria</th>
                    <th>Pagina</th>
                    <th>Descripcion</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((log) => (
                    <tr key={log.id}>
                      <td className={styles.dateCell}>{formatDate(log.creadoEn)}</td>
                      <td className={styles.emailCell} title={log.uid}>
                        {log.email || log.uid || '-'}
                      </td>
                      <td>
                        <Badge variant="neutral" size="sm">
                          {log.rol}
                        </Badge>
                      </td>
                      <td>
                        <Badge variant={accionVariant(log.accion)} size="sm">
                          {log.accion}
                        </Badge>
                      </td>
                      <td>
                        <Badge
                          variant={categoriaVariant(log.categoria)}
                          size="sm"
                        >
                          {CATEGORIA_LABELS[log.categoria] || log.categoria}
                        </Badge>
                      </td>
                      <td className={styles.pageCell}>{log.pagina || '-'}</td>
                      <td className={styles.descCell}>{log.descripcion || '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </MainLayout>
  );
}
